// packages/web-ui/src/components/kb-browser/KbEntityTree.tsx
//
// Story W4-S5 (AC #3) — hierarchical KB entity tree.
//
// Renders entities grouped by parent qualified-name path then sorted by
// kind. Each leaf row gets a kind-icon, the entity name (with search-token
// highlight), and an optional responsibleEngineer suffix. Click + right-click
// handlers are exposed via props; arrow-key navigation moves focus across
// visible rows (roving tabindex).

import type { JSX, MouseEvent, KeyboardEvent } from 'react';
import { useMemo, useRef, useState } from 'react';
import type { KbEntity, KbEntityKind } from '@tessa-lean/base/api-schemas/index.js';
import { groupByHierarchy, ROOT_GROUP_KEY } from './group-by-hierarchy.js';
import { groupByLevel } from './group-by-level.js';
import { highlightMatch } from './highlight-match.js';
import { rankEntities } from './rank-entities.js';

/** Tree grouping mode — exposed via prop so the host (KbMode) drives the toggle. */
export type KbTreeGroupMode = 'level' | 'parent';

const PERFORMANCE_WARN_THRESHOLD = 5000;

/**
 * Maps a kind + qualified-name depth to an entity-row icon. Drives off
 * `qualifiedName` segment count so deeply-nested parts render with the
 * "component" glyph — parity with the lean-sys-jarvis "Knowledge Base
 * Explorer" UX (lines 343–381).
 */
function iconFor(entity: KbEntity): string {
  const depth = entity.qualifiedName.split('::').length;
  switch (entity.kind) {
    case 'part': {
      if (depth >= 3) return '\u{1F539}'; // 🔹 component
      if (depth === 2) return '\u{1F536}'; // 🔶 sub-system
      return '\u{1F537}'; // 🔷 system
    }
    case 'part-usage':
      return '\u{1F4CD}'; // 📍 spec — value-bound usage
    case 'port':
      return '◆'; // ◆
    case 'interface':
    case 'interface-def':
      return '\u{1F50C}'; // 🔌 (F1: interface-def shares the family glyph)
    case 'connection':
    case 'connection-def': {
      const flowType =
        entity.attributes !== undefined && typeof entity.attributes['flowType'] === 'string'
          ? (entity.attributes['flowType'] as string).toLowerCase()
          : undefined;
      if (flowType === 'energy') return '⚡'; // ⚡
      if (flowType === 'material') return '\u{1F4A7}'; // 💧
      if (flowType === 'information') return '\u{1F4E1}'; // 📡
      if (flowType === 'mechanical') return '\u{1F517}'; // 🔗
      return '\u{1F517}'; // 🔗 fallback (F1: connection-def shares the family glyph)
    }
    case 'value-attribute':
      return 'ƒ'; // ƒ
  }
}

function responsibleEngineer(entity: KbEntity): string | undefined {
  const attrs = entity.attributes;
  if (attrs === undefined) return undefined;
  const raw = attrs['responsibleEngineer'];
  if (typeof raw === 'string' && raw.length > 0) return raw;
  if (raw !== null && typeof raw === 'object') {
    const name = (raw as { name?: unknown }).name;
    if (typeof name === 'string' && name.length > 0) return name;
  }
  return undefined;
}

export interface KbEntityTreeProps {
  /** Entities already filtered server-side by kind + search. */
  entities: KbEntity[];
  /** Current search query — empty when search is idle. Drives highlight + rank. */
  query: string;
  /** Currently-selected entity id; the matching row gets `aria-selected`. */
  selectedEntityId?: string;
  /** Entity ids that should pulse for a recent kb:changesetApplied event. */
  recentlyChangedEntityIds?: ReadonlySet<string>;
  /** Click handler — fires on row click and Enter/Space keystroke. */
  onSelectEntity?: (entityId: string) => void;
  /** Right-click handler — receives the row id and click coordinates. */
  onContextMenu?: (entityId: string, x: number, y: number) => void;
  /**
   * Grouping mode. `'level'` (default) groups by `KBEntity.level` (Level 1 →
   * Level 4 → Other). `'parent'` preserves the W4-S5 parent-qualifiedName
   * grouping for users who prefer the package/composition view.
   */
  groupMode?: KbTreeGroupMode;
}

interface VisibleRow {
  entityId: string;
  entity: KbEntity;
  groupKey: string;
}

export function KbEntityTree(props: KbEntityTreeProps): JSX.Element {
  const {
    entities,
    query,
    selectedEntityId,
    recentlyChangedEntityIds,
    onSelectEntity,
    onContextMenu,
    groupMode = 'level',
  } = props;

  const [collapsedGroups, setCollapsedGroups] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [focusedEntityId, setFocusedEntityId] = useState<string | undefined>(undefined);
  // MED-7 — Refs for each group summary button so leaf ArrowLeft can move
  // focus to the parent header per the WAI-ARIA two-step treeitem pattern.
  const groupSummaryRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const groups = useMemo(() => {
    const grouper = groupMode === 'level' ? groupByLevel : groupByHierarchy;
    if (query.length === 0) return grouper(entities);
    // Search-mode: rank first, then group on the ranked order — preserves
    // the ranking signal across groups by emitting groups in the order the
    // ranked entities first appear.
    const ranked = rankEntities(entities, query);
    return grouper(ranked);
  }, [entities, query, groupMode]);

  // Visible rows in render order (collapsed groups skipped). Drives keyboard nav.
  const visibleRows: VisibleRow[] = useMemo(() => {
    const rows: VisibleRow[] = [];
    for (const group of groups) {
      if (collapsedGroups.has(group.key)) continue;
      for (const entity of group.entities) {
        rows.push({ entityId: entity.entityId, entity, groupKey: group.key });
      }
    }
    return rows;
  }, [groups, collapsedGroups]);

  // Performance hint — tree is hand-rolled, virtualisation lands later.
  const warnedRef = useRef(false);
  if (entities.length > PERFORMANCE_WARN_THRESHOLD && !warnedRef.current) {
    warnedRef.current = true;
    // Intentional perf warning per AC #3.8 — informs callers they have crossed
    // the hand-rolled tree's render budget. The webview-ui ESLint config does
    // not ban console in this surface; explicit warn is the simplest contract.
    console.warn(
      `[KbEntityTree] ${String(entities.length)} entities exceed the ${String(PERFORMANCE_WARN_THRESHOLD)}-entity virtualisation threshold; expect render lag.`,
    );
  }

  const toggleGroup = (key: string): void => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const moveFocus = (delta: number, jumpToEdge: 'start' | 'end' | null): void => {
    if (visibleRows.length === 0) return;
    const currentIndex =
      focusedEntityId === undefined
        ? -1
        : visibleRows.findIndex((r) => r.entityId === focusedEntityId);
    let nextIndex: number;
    if (jumpToEdge === 'start') nextIndex = 0;
    else if (jumpToEdge === 'end') nextIndex = visibleRows.length - 1;
    else if (currentIndex === -1) nextIndex = delta > 0 ? 0 : visibleRows.length - 1;
    else
      nextIndex = Math.min(
        Math.max(0, currentIndex + delta),
        visibleRows.length - 1,
      );
    const target = visibleRows[nextIndex];
    if (target !== undefined) setFocusedEntityId(target.entityId);
  };

  const handleRowClick = (entityId: string): void => {
    setFocusedEntityId(entityId);
    onSelectEntity?.(entityId);
  };

  const handleRowContextMenu = (
    entity: KbEntity,
    event: MouseEvent<HTMLLIElement>,
  ): void => {
    event.preventDefault();
    setFocusedEntityId(entity.entityId);
    onContextMenu?.(entity.entityId, event.clientX, event.clientY);
  };

  const handleRowKeyDown = (
    entity: KbEntity,
    groupKey: string,
    event: KeyboardEvent<HTMLLIElement>,
  ): void => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        handleRowClick(entity.entityId);
        return;
      case 'ArrowDown':
        event.preventDefault();
        moveFocus(1, null);
        return;
      case 'ArrowUp':
        event.preventDefault();
        moveFocus(-1, null);
        return;
      case 'Home':
        event.preventDefault();
        moveFocus(0, 'start');
        return;
      case 'End':
        event.preventDefault();
        moveFocus(0, 'end');
        return;
      case 'ArrowLeft':
        event.preventDefault();
        // MED-7 / WAI-ARIA treeitem pattern: ArrowLeft on a leaf moves focus
        // to the parent group header FIRST (no collapse). A second ArrowLeft
        // press on the group summary collapses the group — handled by the
        // summary's own keydown handler below.
        groupSummaryRefs.current.get(groupKey)?.focus();
        return;
      case 'ArrowRight':
        event.preventDefault();
        if (collapsedGroups.has(groupKey)) toggleGroup(groupKey);
        return;
      default:
    }
  };

  if (entities.length === 0) {
    return (
      <div data-testid="kb-tree-empty" className="px-3 py-2 text-xs text-taupe-700">
        No results match.
      </div>
    );
  }

  return (
    <ul
      role="tree"
      aria-label="KB entities"
      data-testid="kb-tree"
      className="flex flex-col"
    >
      {groups.map((group) => {
        const collapsed = collapsedGroups.has(group.key);
        const groupId = `kb-group-${group.key.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
        return (
          <li
            key={group.key}
            role="treeitem"
            aria-expanded={!collapsed}
            data-testid={`kb-group-${group.key}`}
            className="flex flex-col"
          >
            <button
              type="button"
              data-testid={`kb-group-summary-${group.key}`}
              ref={(el): void => {
                // MED-7 — track group summary refs so leaf ArrowLeft can shift
                // focus here per the WAI-ARIA treeitem two-step pattern.
                if (el === null) groupSummaryRefs.current.delete(group.key);
                else groupSummaryRefs.current.set(group.key, el);
              }}
              className="flex items-center gap-1 border-b border-taupe-300 bg-cream-100 px-2 py-1 text-left text-xs font-semibold hover:bg-cream-200"
              onClick={() => {
                toggleGroup(group.key);
              }}
              onKeyDown={(e) => {
                // MED-7 — Step 2 of the WAI-ARIA pattern: ArrowLeft on a
                // focused (expanded) group summary collapses the group.
                // ArrowRight on a collapsed summary expands it.
                if (e.key === 'ArrowLeft') {
                  if (!collapsedGroups.has(group.key)) {
                    e.preventDefault();
                    toggleGroup(group.key);
                  }
                } else if (e.key === 'ArrowRight') {
                  if (collapsedGroups.has(group.key)) {
                    e.preventDefault();
                    toggleGroup(group.key);
                  }
                }
              }}
              aria-controls={groupId}
            >
              <span aria-hidden="true">{collapsed ? '▸' : '▾'}</span>
              <span>{group.key === ROOT_GROUP_KEY ? '(root)' : group.key}</span>
              <span className="ml-auto text-taupe-700">
                {String(group.entities.length)}
              </span>
            </button>
            {collapsed ? null : (
              <ul role="group" id={groupId} className="flex flex-col">
                {group.entities.map((entity) => {
                  const isSelected = selectedEntityId === entity.entityId;
                  const isFocused = focusedEntityId === entity.entityId;
                  const isPulsing =
                    recentlyChangedEntityIds?.has(entity.entityId) ?? false;
                  const engineer = responsibleEngineer(entity);
                  return (
                    <li
                      key={entity.entityId}
                      role="treeitem"
                      aria-selected={isSelected}
                      data-testid={`kb-row-${entity.entityId}`}
                      data-kind={entity.kind}
                      data-pulse={isPulsing ? 'true' : 'false'}
                      tabIndex={isFocused ? 0 : -1}
                      onClick={() => {
                        handleRowClick(entity.entityId);
                      }}
                      onContextMenu={(e) => {
                        handleRowContextMenu(entity, e);
                      }}
                      onKeyDown={(e) => {
                        handleRowKeyDown(entity, group.key, e);
                      }}
                      onFocus={() => {
                        setFocusedEntityId(entity.entityId);
                      }}
                      className={[
                        'flex items-center gap-2 px-3 py-1 text-[13px] cursor-pointer',
                        'border-l-2',
                        isSelected
                          ? 'bg-[var(--accent-cream-active)] border-taupe-700'
                          : 'border-transparent hover:bg-cream-200',
                        isPulsing ? 'animate-pulse-once' : '',
                      ].join(' ')}
                    >
                      <span aria-hidden="true" data-testid={`kb-row-icon-${entity.entityId}`}>
                        {iconFor(entity)}
                      </span>
                      <span className="truncate">{highlightMatch(entity.qualifiedName, query)}</span>
                      {engineer !== undefined ? (
                        <span className="ml-auto truncate text-taupe-700">— {engineer}</span>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Test/util — exposes the kind→icon mapping so unit tests can assert without
 * duplicating the lookup table.
 */
export function _kindIcon(kind: KbEntityKind): string {
  return iconFor({ entityId: '', qualifiedName: 'a', kind, filePath: '' });
}
